import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { Printer } from "lucide-react";

const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_20e37a3a-e6c9-4384-b519-a00f19f82c1f/artifacts/f12i0vth_Screenshot_20260211_021213_Chrome.jpg";

export default function EmploymentLetter() {
  const letterRef = useRef(null);
  const handlePrint = () => { window.print(); };
  const formattedDate = "March 15, 2026";
  const signedDate = "March 15, 2026";

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&display=swap');
        @media print {
          .no-print { display: none !important; }
          body { margin: 0; padding: 0; }
          .print-page { box-shadow: none !important; margin: 0 !important; border-radius: 0 !important; max-width: 100% !important; padding: 0 !important; }
          .letter-body { font-size: 11pt !important; }
          .letter-header { padding: 0.4in 0.6in 0.25in !important; }
          .letter-content { padding: 0.3in 0.6in !important; }
          .letter-footer { padding: 0 0.6in 0.3in !important; }
          [class*="fixed"] { display: none !important; }
        }
        @media screen {
          .print-page { max-width: 8.5in; min-height: 11in; }
        }
        @page { margin: 0.4in 0.5in; size: letter; }
      `}</style>

      <div className="no-print fixed top-4 right-4 z-50">
        <Button onClick={handlePrint} className="bg-blue-600 hover:bg-blue-700 shadow-lg rounded-full px-6" data-testid="print-letter-btn">
          <Printer className="w-4 h-4 mr-2" /> Print / Save as PDF
        </Button>
      </div>

      <div className="min-h-screen bg-gray-100 py-6 px-4 flex justify-center">
        <div ref={letterRef} className="print-page bg-white w-full shadow-2xl rounded-lg flex flex-col" style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}>
          
          {/* Header */}
          <div className="letter-header border-b-[3px] border-red-600 px-10 pt-6 pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src={COMPANY_LOGO} alt="Easy Transportation" className="w-14 h-14 rounded-lg object-cover" />
                <div>
                  <h1 className="text-2xl font-bold tracking-tight" style={{ color: "#B91C1C", fontFamily: "'Outfit', 'Georgia', sans-serif" }}>Easy Transportation</h1>
                  <p className="text-xs text-gray-500 italic" style={{ fontFamily: "'Georgia', serif" }}>Easy Ride, Exceptional Experience</p>
                </div>
              </div>
              <div className="text-right text-[10px] text-gray-600 leading-snug">
                <p className="font-semibold text-gray-800">1115 Logan Ave</p>
                <p>Belvidere, IL 61008</p>
                <p>(815) 914-8139</p>
                <p>Miketransportsj1@gmail.com</p>
                <p className="text-red-700">easytransportationj1.com</p>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="letter-content letter-body flex-1 px-10 py-5 text-gray-800" style={{ fontSize: "11.5pt", lineHeight: "1.5" }}>
            <p className="text-right text-xs text-gray-600 mb-5">{formattedDate}</p>

            <div className="mb-5" style={{ lineHeight: "1.4" }}>
              <p className="font-semibold text-sm">Secretary of State</p>
              <p className="text-sm">State of Illinois</p>
              <p className="text-sm">Springfield, IL 62756</p>
            </div>

            <p className="mb-4 font-semibold" style={{ fontSize: "12pt", color: "#1F2937" }}>
              RE: Verification of Employment — Michael Clarke
            </p>

            <p className="mb-3">To Whom It May Concern,</p>

            <p className="mb-3 text-justify">
              This letter serves as official verification that <strong>Michael Clarke</strong> has been engaged as an
              <strong> Independent Contractor (1099)</strong> with <strong>Easy Transportation</strong> since
              <strong> April 2018</strong>.
            </p>

            <p className="mb-3 text-justify">
              In his capacity as an Independent Contractor, Mr. Clarke provides shuttle driving services,
              transporting passengers from Chicagoland-area airports — including Chicago O'Hare International
              Airport (ORD) and Chicago Midway International Airport (MDW) — to various destinations in
              Wisconsin Dells, Wisconsin, and surrounding areas.
            </p>

            <p className="mb-3 text-justify">
              Mr. Clarke's work schedule varies based on operational demand. His shifts are predominantly
              during nighttime hours, with increased activity during the J1 Work &amp; Travel Visa travel season,
              which typically runs from May through October each year.
            </p>

            <p className="mb-3 text-justify">
              As an independent contractor, Mr. Clarke is responsible for his own tax obligations and
              receives compensation on a per-trip basis. He has consistently demonstrated professionalism,
              reliability, and a commitment to safe transportation services throughout his tenure with our company.
            </p>

            <p className="mb-3 text-justify">
              Should you require any additional information or documentation, please do not hesitate
              to contact our office at <strong>(815) 914-8139</strong> or via email at{" "}
              <strong>Miketransportsj1@gmail.com</strong>.
            </p>

            <p className="mb-4">Sincerely,</p>

            {/* Signature */}
            <div className="mt-4">
              <p className="text-3xl" style={{ fontFamily: "'Dancing Script', cursive", color: "#1a3a6b", fontWeight: 700 }}>Micheno Lawrence</p>
              <div className="w-56 border-b-2 border-gray-800 mt-0.5 mb-1"></div>
              <p className="font-bold text-sm">Micheno Lawrence</p>
              <p className="text-xs text-gray-600">Operations Manager — Easy Transportation</p>
              <p className="text-xs text-gray-600">(815) 914-8139</p>
              <p className="text-[10px] text-gray-400 mt-1 italic">Electronically signed on {signedDate}</p>
            </div>
          </div>

          {/* Footer */}
          <div className="letter-footer px-10 pb-4">
            <div className="border-t-2 border-red-600 pt-2 flex items-center justify-between">
              <p className="text-[9px] text-gray-400">1115 Logan Ave, Belvidere, IL 61008 &nbsp;|&nbsp; (815) 914-8139 &nbsp;|&nbsp; easytransportationj1.com</p>
              <p className="text-[9px] text-gray-400">&copy; {new Date().getFullYear()} Easy Transportation</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
